import { RequestBodyType, type RequestFunctionParams } from 'yapi-to-typescript'
import { captureError, addBreadcrumb } from '../utils/sentry'
import { useGlobalError } from '../hooks/useGlobalError'
import { getAuthInfo, buildAuthQuery } from '../hooks/useAuthInfo'

export interface RequestOptions {
  /**
   * 是否返回 Blob 结果，适用某些返回文件流的接口。 默认false
   */
  returnBlob?: boolean
  /**
   * 是否要关闭全局拦截的notification提示， 默认false
   */
  closeNotification?: boolean
  /**
   * 直接返回 json
   * @default false
   */
  returnJson?: boolean
  /**
   * 请求超时时间，单位毫秒，默认 10000（10s）
   */
  timeout?: number
}
const defaultMessage = '接口服务发生异常'

// 全局错误提示函数
function notifyError(options: { message: string; description?: string }) {
  try {
    const { showGlobalError } = useGlobalError()
    showGlobalError(
      options.message || defaultMessage,
      options.description || ''
    )
  } catch (error) {
    // 如果全局状态管理出现问题，则使用fallback方案
    addBreadcrumb({
      message: '全局错误提示失败',
      category: 'app/request',
      level: 'error',
      data: {
        message: options.message,
        description: options.description
      }
    })
    if (error instanceof Error) {
      captureError(error, {
        feature: 'request-notify-error',
        message: options.message,
        description: options.description
      })
    }
  }
}

/**
 * 拼接中转请求 URL
 * @param baseUrl 网关前缀（形如 `.../gateway.json?reqId=11040&m=`）；
 *                为空串表示 DEV 直连（走 vite proxy），此时不追加任何参数
 * @param path 接口路径，可能已自带 query
 * @param authQuery 客户端认证参数片段（不含起始分隔符），为空则不加
 */
export function buildRequestUrl(
  baseUrl: string,
  path: string,
  authQuery = ''
): string {
  let url = `${baseUrl}${path}`
  if (baseUrl && path.indexOf('?') > -1) {
    url = `${baseUrl}${path.replace(/\?/, '&')}`
  }
  if (baseUrl && authQuery) {
    url += `${url.indexOf('?') > -1 ? '&' : '?'}${authQuery}`
  }
  return url
}

export default async function request<T>(
  payload: RequestFunctionParams,
  options: RequestOptions = {
    closeNotification: false,
    returnBlob: false,
    returnJson: false,
    timeout: 10000
  }
): Promise<T> {
  // 基本地址
  // const baseUrl = payload.prodUrl
  // 请求地址
  let baseUrl =
    'https://app.sohu-inc.com/sys/urlrdt/gateway.json?reqId=11040&m='
  if (payload.method.toLowerCase() === 'post') {
    baseUrl =
      'https://app.sohu-inc.com/sys/urlrdt/gateway-v2.json?reqId=10850&m='
  }

  if (import.meta.env.DEV) {
    baseUrl = ''
  }

  let path = payload.path

  //TODO:仅开发时使用, 生产环境下需要注释掉
  // if (path.startsWith('/api/app')) {
  //   path = path.replace('/api/app', '/api')
  // }

  // 仅走网关（生产）时携带客户端认证参数；DEV 下 baseUrl 为空、走 proxy 直连后端，
  // 无需也无谓去调原生桥
  const authQuery = baseUrl ? buildAuthQuery(await getAuthInfo()) : ''
  const url = buildRequestUrl(baseUrl, path, authQuery)

  // fetch 选项
  const fetchOptions: RequestInit = {
    method: payload.method
  }
  // 超时控制（浏览器端使用 AbortController）
  const controller = new AbortController()
  // const timeoutMs = options.timeout ?? 20000
  const timeoutMs = 20000
  fetchOptions.signal = controller.signal as any
  if (payload.hasFileData) {
    fetchOptions.headers = {}
    fetchOptions.body = payload.getFormData()
  } else if (payload.requestBodyType === RequestBodyType.json) {
    fetchOptions.headers = {
      'Content-Type': 'application/json; charset=UTF-8'
    }
    fetchOptions.body = JSON.stringify(payload.data)
  } else if (payload.requestBodyType === RequestBodyType.form) {
    fetchOptions.headers = {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    }
    fetchOptions.body = Object.keys(payload.data).reduce(
      (prev, cur, i) =>
        payload.data[cur] === null || payload.data[cur] === undefined
          ? prev
          : prev +
            (i === 0 ? '' : '&') +
            `${encodeURIComponent(cur)}=${encodeURIComponent(
              payload.data[cur]
            )}`,
      ''
    )
  }

  if (!fetchOptions.headers) {
    fetchOptions.headers = {}
  }

  ;(fetchOptions.headers as any)['x-hxe-code'] =
    // @ts-ignore
    globalThis._hxeCode ||
    sessionStorage.getItem('hxeCode') ||
    document.cookie
      .split('; ')
      .find(row => row.startsWith('hxeCode='))
      ?.split('=')[1] ||
    //TODO:仅开发时使用
    // 'a1e80c3b-6e02-449d-a12d-9f0d12558fa6'
    ''
  try {
    // 启动超时计时器
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    const res = await fetch(url, fetchOptions)

    // 请求成功，清除超时计时器
    clearTimeout(timeoutId)

    if (res.status === 401) {
      // 添加面包屑
      addBreadcrumb({
        message: `API Request: ${payload.method} ${url}`,
        category: 'http',
        level: 'info',
        data: {
          method: payload.method,
          url,
          status: 401
        }
      })
      const message = '认证信息错误，请重新登录'
      if (!options.closeNotification)
        notifyError({ message: defaultMessage, description: message })
      // todo: 实现无权限页面报错
    }
    if (
      (res.status === 301 || res.status === 302 || res.status === 307) &&
      res.headers.get('location')
    ) {
      // 添加面包屑
      addBreadcrumb({
        message: `API Request: ${payload.method} ${url}`,
        category: 'http',
        level: 'info',
        data: {
          method: payload.method,
          url,
          status: res.status
        }
      })
      window.location.href = res.headers.get('location')!
      //@ts-ignore
      return
    }
    if (res.status >= 300 || res.status < 200) {
      const message = '当前接口异常，请稍后重试！'
      const error = new Error(`HTTP ${res.status}: ${message}`)

      // 捕获HTTP错误
      captureError(error, {
        url,
        method: payload.method,
        status: res.status,
        statusText: res.statusText,
        requestData: payload.data,
        type: 'HttpError'
      })

      if (!options.closeNotification)
        notifyError({ message: defaultMessage, description: message })
      throw error
    }
    // 文件处理
    if (options?.returnBlob) {
      // 添加面包屑
      addBreadcrumb({
        message: `API Request: ${payload.method} ${url}`,
        category: 'http',
        level: 'info',
        data: {
          method: payload.method,
          url,
          status: res.status,
          error: 'h5 目前没有返回文件一说'
        }
      })
      return (await res.blob()) as any
    }
    // json处理
    if (res.headers.get('Content-Type')?.includes('application/json')) {
      const json = await res.json().then(r => r)
      // 添加面包屑
      addBreadcrumb({
        message: `API Request: ${payload.method} ${url}`,
        category: 'http',
        level: 'info',
        data: {
          method: payload.method,
          url,
          status: res.status,
          req: payload?.data ?? null,
          res: json
        }
      })
      if (options?.returnJson) return json
      if (json?.code === 0 || json?.code === 200) return json?.data
      const message = json?.msg ?? '当前接口异常，请稍后重试！'
      const error = new Error(json?.msg ?? '当前接口异常，请稍后重试！')

      // 捕获业务错误
      captureError(error, {
        url,
        method: payload.method,
        businessCode: json?.code,
        businessMessage: json?.msg,
        requestData: payload.data,
        responseData: json,
        type: 'HttpError'
      })

      if (!options.closeNotification)
        notifyError({ message: defaultMessage, description: message })
      throw error
    }
    // text
    // return await res.text();
    // other error
    const message = '当前接口异常，请稍后重试！'
    const error = new Error(message)

    // 捕获其他错误
    captureError(error, {
      url,
      method: payload.method,
      contentType: res.headers.get('Content-Type'),
      requestData: payload.data,
      type: 'HttpError'
    })

    if (!options.closeNotification)
      notifyError({ message: defaultMessage, description: message })
    throw error
  } catch (error) {
    // 超时中止产生的错误
    if ((error as any)?.name === 'AbortError') {
      const message = '请求超时，请稍后重试'
      const timeoutError = new Error(message)
      captureError(timeoutError, {
        url,
        method: payload.method,
        requestData: payload.data,
        type: 'Timeout'
      })

      if (!options.closeNotification)
        notifyError({ message: defaultMessage, description: message })
      throw timeoutError
    }
    // 捕获网络错误等其他异常
    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError = new Error('网络连接异常，请检查网络设置')
      captureError(networkError, {
        url,
        method: payload.method,
        originalError: error.message,
        requestData: payload.data,
        type: 'HttpError'
      })

      if (!options.closeNotification)
        notifyError({
          message: defaultMessage,
          description: '网络连接异常，请检查网络设置'
        })
      throw networkError
    }

    captureError(error as Error, {
      url,
      method: payload.method,
      originalError: (error as Error).message,
      requestData: payload.data,
      type: 'HttpError'
    })

    // 重新抛出已处理的错误
    throw error
  }
}
